import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Tabs, message } from "antd"

import * as api from "../../../../api"

import EmptyData from "../../../_shared/components/EmptyData"

import useRestList from "../../../_shared/hooks/useRestList.js"
import { useStateValue } from "../../../_shared/context/AppStateStore"

import TicketPreview from "./TicketPreview"
import TicketDetail from "./TicketDetail"

// ----------------------------------------------------------------------------

const { TabPane } = Tabs

// ----------------------------------------------------------------------------

function Tickets(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [{ token }] = useStateValue()

  const [openTickets, fetchOpenTickets] = useRestList("/tickets", token, {
    status: "open",
  })

  const [closeTickets, fetchCloseTickets] = useRestList("/tickets", token, {
    status: "closed",
  })

  const [selectedTicket, setSelectedTicket] = useState(null)

  const currentTab = useRef("open")

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    if (selectedTicket) {
      const ticket = openTickets?.find(
        (ticket) => ticket.id === selectedTicket.id
      )

      setSelectedTicket(ticket)
    }

    //eslint-disable-next-line
  }, [openTickets])

  useEffect(() => {
    if (selectedTicket) {
      markTicketMessagesAsRead()
    }

    //eslint-disable-next-line
  }, [selectedTicket])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  async function refreshTickets() {
    fetchOpenTickets()
    fetchCloseTickets()
  }

  async function markTicketMessagesAsRead() {
    try {
      if (selectedTicket.id && selectedTicket.unreadedMessages) {
        await api.markTicketMessagesAsRead(selectedTicket.id, token)

        fetchOpenTickets()
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function handleCreateNewTicketMessage(ticketId, sentBy, text) {
    try {
      await api.createTicketMessage(ticketId, sentBy, text, token)

      fetchOpenTickets()
    } catch (error) {
      message.error("Errore nel salvataggio del nuovo messaggio")
    }
  }

  function handleTicketPreviewClick(ticketId) {
    const currentList =
      currentTab?.current === "open" ? openTickets : closeTickets

    const ticket = currentList?.find((ticket) => ticket.id === ticketId)

    setSelectedTicket(ticket)
  }

  function handleCurrentTabChange(key) {
    currentTab.current = key

    setSelectedTicket(null)
  }

  // -------------------------------------

  function renderTickets(status) {
    const list = status === "open" ? openTickets : closeTickets

    if (list && list.length > 0) {
      return list.map((ticket) => (
        <TicketPreview
          className="ticket-card"
          key={ticket.id}
          id={ticket.id}
          subject={ticket.subject}
          description={ticket.description}
          updatedAt={ticket.updatedAt}
          onClick={handleTicketPreviewClick}
          selected={selectedTicket?.id === ticket.id}
          unreadedMessages={ticket.unreadedMessages}
        />
      ))
    } else {
      return <EmptyData description="Nessun messaggio." />
    }
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <div className={props.className}>
      <section className="column list">
        <Tabs defaultActiveKey="open" onChange={handleCurrentTabChange}>
          <TabPane tab="Aperti" key="open">
            {renderTickets("open")}
          </TabPane>
          <TabPane tab="Chiusi" key="close">
            {renderTickets("close")}
          </TabPane>
        </Tabs>
      </section>

      <section className="column detail">
        <TicketDetail
          ticket={selectedTicket}
          onCreateMessage={handleCreateNewTicketMessage}
          onCloseTicket={refreshTickets}
        />
      </section>
    </div>
  )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

Tickets.propTypes = {
  className: PropTypes.string.isRequired,
}

Tickets.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledTickets = styled(Tickets)`
  & {
    display: flex;
    flex-direction: row;

    /* some shitty hardcoded computations */
    height: calc(100vh - 64px - 48px - 35px);
    max-height: calc(100vh - 64px - 48px - 35px);
    overflow: hidden;

    .column {
      flex: 1;

      min-height: 100%;
    }

    .list {
      margin-right: 20px;
      min-height: 100%;
      max-height: 100%;

      max-width: 405px;

      overflow-y: auto;

      -ms-overflow-style: none;

      ::-webkit-scrollbar {
        display: none;
      }

      .ant-tabs {
        min-height: 100%;

        .ant-tabs-bar {
          margin-bottom: 25px;

          border-color: ${(props) =>
            props.theme.colors.tertiaryBackgroundColor};
        }

        .ticket-card {
          margin-bottom: 12px;
        }
      }
    }

    .detail {
      margin-left: 10px;
      min-height: 100%;
      max-height: 100%;
    }
  }
`
// ----------------------------------------------------------------------------

export default StyledTickets
